import type { AccessGroup } from '@/api/auth';
import type { QuestionSet } from '@/api/questionSets';

/**
 * The one access question §6.9 asks, against the rows of `backend/tests/test_permission_matrix.py`
 * that answer it.
 *
 * ⚠️ **This decides what is *rendered*, never what the API allows** — the standing obligation of
 * 2026-07-25, which `RequireGroup`, `candidateAccess.ts` and `vacancyAccess.ts` all state. The client
 * gate is never the evidence.
 *
 * ⚠️ **`questionBankAccess.ts` stood beside this module and is deleted** (2026-09-24, ADR 0019). It
 * held `mayWriteQuestion` — *"the author, or an `Admin`"*, asked per **question** row on
 * `QuestionOut.author_id` — and there is no such field and no such rule any more: **there is one write
 * gate and it is the set's** (ADR 0019; ADR 0016 decision 5(c): composing is writing the set, not the
 * questions in it). A predicate over a question's own author would now be a second definition of a
 * rule that has one, evaluated on a field the payload no longer carries. `Viewer` moved here with it,
 * because this is where the predicate that needs it lives.
 *
 * ⚠️ **`mayReadQuestionBank` is gone too, and its rule was *reversed* rather than relaxed**
 * (2026-08-11, ADR 0018). It was `groups.includes('Admin')`, standing on `Route("get", BANK, None,
 * (401, 403, 403, 200, 403, 403))` — a **Recruiter was 403 on the bank**. All three groups are
 * admitted at the door on this surface and the **rows** are narrowed by the server, so there is no
 * read predicate here and none at the route: a gate over all three groups is a gate that can never
 * refuse.
 */

/**
 * Whether this viewer may **write** this question set — and therefore everything in it: its name, its
 * state, and every question it owns.
 *
 * The server's rule, mirrored exactly. `author_id` is set once at creation from the session user and
 * is not writable through `PATCH`, so it is a stable fact about the row rather than something a client
 * could assert.
 *
 * ⚠️ **An `author_id` of `null` is an `Admin`'s alone, and on this table that is the *ordinary* case
 * rather than the edge one.** Every set the ADR 0018 migration created has one — the migration could
 * not invent an author for a bank a vacancy owned — so on a migrated tenant *every* set is
 * author-less. A predicate that read `null` as *"nobody, so anybody"* would offer a `Recruiter` a
 * rename control over the whole of that tenant's sets and answer them 403 on the write.
 *
 * ⚠️ **§5 decides the rest: hidden, never disabled.** A viewer who is neither the author nor an
 * `Admin` sees no rename, retire, restore, add, edit, remove, restore-question or reorder control at
 * all — not a greyed one. The set itself is theirs to read, if the reach predicate admits it; only the
 * writes on it are not. And because the gate is the **set's**, the actions column is all-or-nothing
 * per screen rather than per row: a column that appeared and vanished row by row would be a layout
 * moving for a reason nobody can see.
 *
 * There is deliberately **no create predicate beside this one**. `POST /api/v1/question-sets/` admits
 * all three groups (ADR 0016 decision 5(c): an `Interviewer` curates and does not assign), so a
 * predicate for *New set* could never be false where it was evaluated — BUG-040's rule.
 *
 * **The client gate is never the evidence**: the server refuses a non-author's write itself, and it
 * answers **404 before 403**, so a set outside the reader's reach is indistinguishable from one that
 * never existed.
 */
export const mayWriteQuestionSet = (set: { author_id: number | null }, viewer: Viewer): boolean =>
  viewer.groups.includes('Admin') || (set.author_id !== null && set.author_id === viewer.id);

/**
 * What the author cell of a row says — **the server's own string, or the fragment that completes the
 * column's own header**.
 *
 * ⚠️ **It is a fragment and not a sentence, because a cell is read through its header** (BUG-095,
 * 2026-09-25). The header is *Written by*, so the cell has to finish it: *"Came with the workspace — no
 * author"*. A live pass measured why the sentence form cannot sit here — below `lg` the table restacks
 * into label/value pairs, ~~so the label sits directly beside the value and the accessible string is the
 * two concatenated (`Table` renders `data-column-label` immediately before the cell)~~, which made
 * *"Written byThis set came with the workspace and has no author."*. It was also the only punctuated
 * cell in the grid and about four times its neighbours' width.
 *
 * ⚠️ **The struck clause was the *mechanism*, and BUG-105 has removed it** _(2026-09-28)_. `Table`
 * no longer renders the label immediately before the cell: a visually hidden `": "` now sits between
 * the column name and the value, so this row announces *"Written by: Came with the workspace — no
 * author"* and nothing concatenates. What was measured on 2026-09-25 stands as a measurement of that
 * day; what no longer stands is reasoning from it about the component as it is. ⚠️ **The decision it
 * supported is unchanged** — the cell is still a fragment, on the other grounds the same paragraph
 * gives and BUG-105 did not touch: it was the only punctuated cell in the grid and about four times
 * its neighbours' width. Struck rather than dropped, because a reader who finds only the surviving
 * half would not know the concatenation argument had ever been made or why it stopped applying.
 *
 * ⚠️ **`author_name: null` and *"Unnamed account"* are two different states and the row must not
 * merge them** (ADR 0016 decision 5(a)). `null` means the set has **no author at all** — every
 * migrated set, and every set whose author's account was deleted — while an account that simply has no
 * name of its own comes back as the string *"Unnamed account"*, resolved by the server through
 * `name_without_an_address`. Rendering the first as the second would tell a reader that a colleague
 * wrote something nobody wrote, and it is exactly the row an `Admin` alone may edit.
 *
 * It takes the fragment rather than reaching for `i18next` so the catalogue stays the only place any
 * interface string is written (§11).
 *
 * ⚠️ **`=== null` and not `??`, so the two helpers test the state with the same operator**
 * _(2026-09-25, a `code-reviewer` round)_. They agree on every inhabitant of the generated type —
 * `author_name` is `string | null`, and the server resolves it through `name_without_an_address`, which
 * answers *"Unnamed account"* rather than `''` — so this was cosmetic. It is done anyway because the
 * sentence below `setAuthorSentence` asserts that the **decision** about an author-less set is
 * single-sourced *by naming the branch both helpers take*, and a claim that is true in substance and
 * false in letter is the kind that stops being checked.
 */
export const setAuthorLabel = (set: Pick<QuestionSet, 'author_name'>, nobody: string): string =>
  set.author_name === null ? nobody : set.author_name;

/**
 * The same fact as a **sentence**, for the one place it is not a cell — §6.9's set screen, where it
 * stands alone under the heading (BUG-095, 2026-09-25).
 *
 * ⚠️ ~~**The author-less string is the cell's own key and stays one key**
 * (`questions:sets.table.noAuthor`); a second entry saying the same thing in two registers is how the
 * table and the screen start disagreeing about what `author_name: null` means.~~ **Struck 2026-09-25,
 * one round after it was written (BUG-095).** The single key was the wrong constraint, and forcing it
 * made the cell wrong instead of the screen: the cell's value was a full punctuated sentence under a
 * *Written by* header, which fights the header in the wide grid and reads as
 * *"Written byThis set came with the workspace and has no author."* in the restacked label/value form a
 * live pass measured below `lg`.
 *
 * ⚠️ **What must not drift is the *decision*, and it does not: it lives here.** `setAuthorLabel` and
 * `setAuthorSentence` both branch on `set.author_name === null` in this module — the same test, written
 * the same way, which is checkable by reading the two expressions — so what an author-less set *is* is
 * defined once whatever the copy says. Two **wordings** for a column cell
 * (`questions:sets.table.noAuthor` — *"Came with the workspace — no author"*) and a standalone sentence
 * (`questions:sets.detail.noAuthor` — *"This set came with the workspace and has no author."*) is
 * ordinary i18n rather than a second definition of the state.
 *
 * `writtenBy` is a function rather than a finished string so the named case is never interpolated for a
 * set that has no name to interpolate; the catalogue is still the only place either sentence is written.
 */
export const setAuthorSentence = (
  set: Pick<QuestionSet, 'author_name'>,
  writtenBy: (author: string) => string,
  nobody: string,
): string => (set.author_name === null ? nobody : writtenBy(set.author_name));

/** Who is looking: the session's user id and their groups, which is all this predicate needs. */
export interface Viewer {
  id: number;
  groups: AccessGroup[];
}
